import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Option } from '../../../core';
import Button from '../../ui/Button';
import Form from '../../ui/Form';
import Modal from '../../ui/Modal';
import SelectInput from '../../ui/SelectInput';
import { FrontdeskPractitionerFormInputs } from '../types';
import AdminBookingPractitionerFormContext from './AdminBookingPractitionerFormContext';
import AdminBookingPractitionerInputs from './AdminBookingPractitionerInputs';
import { renderDefaultAdminBookingPractitionerInputValues } from './helper';

export interface AdminBookingPractitionerFormProps {
  values?: FrontdeskPractitionerFormInputs;
  onSelectPractitioner?: (data?: Option) => void;
  onSubmit?: (data: FrontdeskPractitionerFormInputs) => void;
  onCancel?: () => void;
  shouldReset?: boolean;
  practitioners?: Array<Option>;
  selectedPractitioner?:
    | FrontdeskPractitionerFormInputs
    | Array<FrontdeskPractitionerFormInputs>
    | null;
  isMulti: boolean;
  label?: string;
  isDisabled?: boolean;
  selectedPractitionerOption?: Option | Array<Option> | null;
  addPractitioner?: boolean;
  hpdUrl?: string;
  column?: number;
  isSubmitOnEnter?: boolean;
  loadPractitioners?: any;
}

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CreateNewWrapper = styled(Button)`
  align-self: flex-end;
  justify-self: end;
  height: 36px;
`;

// this is necessary for gatsby to build using the window object
const isBrowser = typeof window !== 'undefined';

const AdminBookingPractitionerForm = ({
  values,
  onCancel,
  onSubmit,
  onSelectPractitioner,
  shouldReset,
  selectedPractitioner,
  isMulti,
  label,
  isDisabled,
  practitioners,
  addPractitioner,
  hpdUrl,
  column,
  isSubmitOnEnter,
  loadPractitioners,
}: AdminBookingPractitionerFormProps) => {
  const [showForm, setShowForm] = useState(false);
  const [selectedPractitionerOption, setSelectedPractitionerOption] = useState<
    Option | Array<Option> | null
  >();
  const data = renderDefaultAdminBookingPractitionerInputValues(values);

  useEffect(() => {
    if (!!selectedPractitioner) {
      if (isMulti && Array.isArray(selectedPractitioner) && practitioners) {
        const providerIds = selectedPractitioner.map(({ id, locationId }) =>
          locationId ? `${id}|${locationId}` : id
        );

        const selectedPractitionerOptions = practitioners.filter((item: Option) =>
          providerIds.includes(item.value)
        );

        return setSelectedPractitionerOption(selectedPractitionerOptions);
      }

      if (practitioners && !Array.isArray(selectedPractitioner)) {
        const selectedOption = practitioners.find(
          (item: Option) => item.value === selectedPractitioner.id
        );

        setSelectedPractitionerOption(selectedOption);
      }
    }
  }, [selectedPractitioner]);

  const handleCancel = () => {
    onSelectPractitioner && onSelectPractitioner();
    setShowForm(false);
    onCancel && onCancel();
  };

  const handleSubmit = (data: FrontdeskPractitionerFormInputs) => {
    setShowForm(false);
    onSubmit && onSubmit(data);
  };

  const handleChange = (selectedOption: Option) => {
    setSelectedPractitionerOption(selectedOption);
    onSelectPractitioner && onSelectPractitioner(selectedOption);
    setShowForm(false);
  };

  // TODO: why?
  // @ts-ignore
  // @ts-ignore
  return (
    <AdminBookingPractitionerFormContext.Provider
      value={{
        selectedPractitioner,
        values: data,
      }}
    >
      <React.Fragment>
        {!showForm && (
          <Form.Row column={column || 2}>
            <SelectInput
              placeholder="Search Health Practitioners"
              label={label || 'Search Health Practitioners'}
              variant="primary"
              isAsync
              isClearable
              loadOptions={loadPractitioners}
              onChange={(selectedOption: Option) => {
                handleChange(selectedOption);
              }}
              isMulti={isMulti}
              disabled={isDisabled}
              options={practitioners}
              // TODO: why?
              //@ts-ignore
              value={selectedPractitionerOption}
            />
            {addPractitioner && (
              <CreateNewWrapper
                size="small"
                variant="secondary"
                onClick={() => isBrowser && window.open(hpdUrl, '_blank')}
              >
                Create New
              </CreateNewWrapper>
            )}
          </Form.Row>
        )}

        {showForm && (
          <Modal>
            <Form
              values={data}
              onCancel={handleCancel}
              onSubmit={handleSubmit}
              shouldReset={shouldReset}
              edit
              isSubmitOnEnter={isSubmitOnEnter}
            >
              <TitleContainer>
                <Form.RowLabel>New Additional Health Practitioner</Form.RowLabel>
              </TitleContainer>

              <AdminBookingPractitionerInputs />
            </Form>
          </Modal>
        )}
      </React.Fragment>
    </AdminBookingPractitionerFormContext.Provider>
  );
};

export default AdminBookingPractitionerForm;
